import { useRef, useState } from "react";
import { LIMIT_TEXT_REPORT } from "../../../utils/vars";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useMessage } from "../../../global-components/src";
import { useAuth } from "../../../global-components/src";
import { errorMessage } from "../../../global-components/src/utils/text";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import { User } from "../../../global-components/src/types";
import { useTranslation } from "react-i18next";

interface WarnUserProps {
    user: User | null;
    onWarned: () => void;
}

const WarnUser: React.FC<WarnUserProps> = ({ user, onWarned }) => {
    const [text, setText] = useState("");
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const { setMessage } = useMessage();
    const { token } = useAuth();
    const { t } = useTranslation();

    const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newText = event.target.value;
        setText(newText);
        setIsLimitExceeded(newText.length > LIMIT_TEXT_REPORT);
    };

    const handleCancel = () => {
        setText("");
    };

    const handleWarnUser = async () => {
        setError("");
        setIsLoading(true);
        const url = `${PACOCA_API_URL}/users/warn/${user?.id}`;

        try {
            if (!text || text.length < 3) {
                throw new Error(t("warnUser.errorRequired"));
            }

            await axios.post(url, { id_user: user?.id, text }, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            if (closeRef.current) closeRef.current.click();

            setText("");
            setMessage(t("warnUser.sent"), "success");
            onWarned();
        } catch (err: any) {
            const messageError = errorMessage(err);
            setError(messageError || t("post.errorSendWarning"));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="modal fade" id="warn-user" tabIndex={-1} aria-labelledby="warn-user" aria-hidden="true">
            <div className="modal-dialog cover-mobile">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{t("warnUser.title", { name: user?.name.split(" ")[0] })}</h5>
                        <button ref={closeRef} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <p>{t("warnUser.description", { name: user?.name.split(" ")[0] })}</p>
                        <label htmlFor="reason" className="label-required">{t("warnUser.reason")}</label>
                        <textarea 
                            style={{ color: isLimitExceeded ? "red" : "inherit" }}  
                            onChange={handleTextChange}  
                            value={text} 
                            className="form-control" 
                            name="reason" 
                            id="reason" 
                            required 
                            cols={30} 
                            rows={5}>
                        </textarea>
                        <div className="user-select-none limite-denuncia">
                            {text.length}/{LIMIT_TEXT_REPORT}
                        </div>
                        {error && <div className="invalid-feedback" style={{ display: "block" }}>{error}</div>}
                    
                        <div className="modal-footer modal-footer-modal">
                            <button type="button" onClick={handleCancel} className="btn btn-secondary btn-modal" data-bs-dismiss="modal">{t("generics.cancel")}</button>
                            <button disabled={isLimitExceeded || isLoading} onClick={handleWarnUser} type="submit" className="btn-modal btn btn-blue">
                                {!isLoading ? (
                                    <>{t("generics.send")}</>
                                ) : (
                                    <Spinner />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarnUser;